import axios from "axios";
import { getBackendURL } from 'src/config/environment'; // Obtiene la URL del backend según entorno (dev/prod)
import { reportRemoteVersion } from 'src/utils/versionCheck';
import { getDeviceIdSync } from 'src/utils/deviceId'; // Installation ID para device binding (marcaciones)
import { HEADER_ACTUAR_COMO_FISCAL, esRutaDeFiscal, obtenerFiscalActuando } from 'src/config/gerenteOperaciones';
// Evento global para notificar expiración de sesión entre módulos
export const SESSION_EXPIRED_EVENT = 'session-expired'; // Nombre del evento personalizado, exportado para ser usado por la App

// Helper: emite evento de sesión expirada con un mensaje opcional
function notifySessionExpired(message?: string) { // Dispara a window un CustomEvent con detalle
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { message } })); // Broadcast del evento
}

// Resolvemos la URL base del backend únicamente desde la configuración
const resolvedBaseURL = getBackendURL(); // usar solo la palanca/entorno efectivo

const api = axios.create({
  // URL base para todas las peticiones al backend
  baseURL: resolvedBaseURL,
  // Tiempo máximo de espera en milisegundos
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || "30000"),
  // Encabezados por defecto para JSON
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Interceptor para manejar respuestas
api.interceptors.response.use(
  (response) => {
    // Heartbeat de version: si el backend cambio (deploy), avisamos al usuario.
    const remoteVersion = response.headers?.['x-app-version'];
    if (remoteVersion) reportRemoteVersion(remoteVersion);

    // Log de respuestas exitosas en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', response.config.url, response.status);
    }
    return response;
  },
  (error) => {
    // Tambien aprovechamos respuestas con error para leer el header.
    const remoteVersion = error?.response?.headers?.['x-app-version'];
    if (remoteVersion) reportRemoteVersion(remoteVersion);

    // Log de errores en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API Error:', error.config?.url, error.response?.status, error.message);
    }

    // Detección de expiración de sesión y notificación global
    const status = error?.response?.status; // Status HTTP devuelto por el backend
    const apiMessage = error?.response?.data?.error || error?.response?.data?.info; // Posible mensaje del backend
    const text = typeof apiMessage === 'string' ? apiMessage.toLowerCase() : ''; // Normalizamos para buscar palabras clave

    // Condición: backend devuelve 400 con texto de sesión expirada o 401 no autorizado
    const looksExpired = (
      (status === 400 && text.includes('sesión') && (text.includes('expir') || text.includes('expiró') || text.includes('expirada'))) // Heurística por mensaje
      || status === 401 // No autorizado generalmente implica token inválido/expirado
    );

    if (looksExpired) {
      // Emitimos evento global para que la App centralice el manejo (limpiar y redirigir)
      notifySessionExpired(typeof apiMessage === 'string' ? apiMessage : 'La sesión ha expirado.'); // Mensaje para el usuario
    }

    throw error;
  }
);

// Interceptor para agregar headers de autenticación si es necesario
api.interceptors.request.use(
  (config) => {
    // Aquí puedes agregar tokens de autenticación si los tienes
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Identificador de dispositivo (Installation ID) para el binding de dispositivo
    // seguro que valida el backend en el login de operarios.
    config.headers['X-Device-Id'] = getDeviceIdSync();
    // Gerente de Operaciones actuando como un fiscal: solo en pantallas de fiscal.
    // El backend lo ignora para cualquier otro cargo o ruta.
    const fiscalActuando = obtenerFiscalActuando();
    if (fiscalActuando && esRutaDeFiscal(window.location.pathname)) {
      config.headers[HEADER_ACTUAR_COMO_FISCAL] = fiscalActuando.cedula;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api;
